import { useEffect, useState } from "react";

import { Logger } from "@/apis/Logger";
import { isPermissionFlowAvailable } from "@/helpers/electron-helper";
import {
  type CapabilityBlockReason,
  type CapabilityStatus,
} from "@/types/agentcore";

/** What the checklist knows about the macOS grants right now. */
export interface GrantStatus {
  /** Grants still outstanding. Empty once macOS has given everything. */
  readonly missingGrants: readonly CapabilityBlockReason[];
  /** False until the first probe settles, so the page never claims a grant it has not read. */
  readonly isResolved: boolean;
  /**
   * Whether the grants were actually read, as opposed to assumed.
   *
   * False when the bridge is absent or the probe threw. Callers must not read the empty list that
   * comes with it as evidence a grant is held: the walkthrough treats it as both outstanding and
   * offers the report as the way on, and the funnel must not score it as a clean pass.
   */
  readonly didProbeAnswer: boolean;
  /**
   * Whether a further attempt is on the clock, so an unanswered probe is not the final word yet.
   *
   * `isResolved` deliberately turns true even with a retry queued, so a surface is never stranded by a
   * dead bridge. A caller gating something the hire cannot take back -- advancing past a step -- reads
   * this as well and keeps waiting.
   */
  readonly isAnotherAttemptQueued: boolean;
  /**
   * Whether recording and the assistant are allowed to proceed.
   *
   * The one composed answer, so the recording gate and the offer gate cannot drift apart. A build
   * with no permission bridge is not held to grants it cannot be asked about.
   */
  readonly areGrantsHeld: boolean;
  /** Re-read the grants. Called when the hire returns from System Settings. */
  readonly refresh: () => void;
}

const NOTHING_MISSING: readonly CapabilityBlockReason[] = [];

/**
 * How long a probe is waited on before it is treated as unanswered.
 *
 * The bridge returns a promise the host settles; nothing guarantees it ever does. Unbounded, a
 * dropped reply leaves `isResolved` false for the life of the page, and the surfaces that wait on it
 * show a spinner with no way out.
 */
const PROBE_DEADLINE_MS = 5_000;

/**
 * A probe that did not answer is retried, because the host may still be starting up.
 *
 * Focus alone is not enough, since a probe that fails while the window already has focus would
 * otherwise never be attempted again for the life of the page. Bounded because the failures worth
 * retrying are transient: a host that has torn its handler down keeps rejecting until the app is
 * relaunched, and the walkthrough treats that as both grants outstanding rather than as recoverable.
 */
const RETRY_DELAY_MS = 2_000;
const MAX_RETRIES = 2;

/** Distinguishes the deadline from a host that resolves its probe with nothing. */
const TIMED_OUT = Symbol("grant probe deadline");

interface GrantSnapshot {
  readonly missingGrants: readonly CapabilityBlockReason[];
  readonly isResolved: boolean;
  readonly didProbeAnswer: boolean;
  /** A further attempt is on the clock, so an unanswered probe is not the final word yet. */
  readonly isAnotherAttemptQueued: boolean;
}

const UNREAD: GrantSnapshot = {
  didProbeAnswer: false,
  isAnotherAttemptQueued: false,
  isResolved: false,
  missingGrants: NOTHING_MISSING,
};

/*
 * One probe for every reader, held at module scope.
 *
 * Three surfaces read the grants at once -- the recording gate, the VPN offer and the walkthrough --
 * and a hook-local copy gave each its own IPC round trip on every window focus. Worse, their answers
 * could disagree within a single commit, so the gate that decides recording and the gate that decides
 * the offer were not always reading the same macOS.
 */
let snapshot: GrantSnapshot = UNREAD;
const listeners = new Set<(next: GrantSnapshot) => void>();
/*
 * Probes overlap: a hire returning from System Settings can fire several focus events, and the grants
 * they just granted are only in the newest answer. Without this an older probe resolving late would
 * put the outstanding grants back on screen after the checklist had cleared them.
 */
let latestProbe = 0;
let retriesLeft = MAX_RETRIES;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

const publish = (next: GrantSnapshot): void => {
  snapshot = next;
  listeners.forEach((listener) => listener(next));
};

const cancelRetry = (): void => {
  if (retryTimer !== undefined) {
    clearTimeout(retryTimer);
    retryTimer = undefined;
  }
};

/**
 * Records that nothing was read, and lines up another attempt while any remain.
 *
 * The last known list is left alone. Callers tell a failure from an answer by `didProbeAnswer`, so an
 * empty list here is never read as evidence a grant is held.
 */
const settleUnanswered = (): void => {
  /*
   * Resolved even with a retry queued, so a page that waits on it is never stranded by a dead bridge.
   * `isAnotherAttemptQueued` is what a caller reads to keep holding a decision the hire cannot take
   * back -- the walkthrough wants to render now, the VPN step's Next button does not.
   */
  const willRetry = retriesLeft > 0 && listeners.size > 0;
  publish({
    ...snapshot,
    didProbeAnswer: false,
    isAnotherAttemptQueued: willRetry,
    isResolved: true,
  });
  if (!willRetry) {
    return;
  }
  retriesLeft -= 1;
  cancelRetry();
  retryTimer = setTimeout(() => {
    retryTimer = undefined;
    probeOnce();
  }, RETRY_DELAY_MS);
};

/**
 * Read the grants once.
 *
 * Every exit settles: a page that waits for `isResolved` must never wait forever, whatever the host
 * does with the call.
 */
function probeOnce(): void {
  const thisProbe = (latestProbe += 1);
  const isSuperseded = (): boolean => latestProbe !== thisProbe;

  const probe = window.electronAPI?.probeAgentCapability;
  if (!probe) {
    publish({
      ...snapshot,
      didProbeAnswer: false,
      isAnotherAttemptQueued: false,
      isResolved: true,
    });
    return;
  }

  let answered: Promise<CapabilityStatus>;
  try {
    /*
     * The try is what matters here: a preload that throws synchronously would otherwise escape into the
     * effect that started this and take the wizard down with an unhandled render error. A payload that
     * is not a promise needs no guard, since the race below coerces it and the shape check rejects it.
     */
    answered = Promise.resolve(probe());
  } catch (error) {
    Logger.warnErrorLike("Grant probe threw before returning", error, {
      metricType: "MacOsGrantProbeFailed",
    });
    settleUnanswered();
    return;
  }

  /*
   * A sentinel rather than `undefined`, which a host is free to resolve with -- read as the deadline
   * it would be reported as a timeout that never happened, and the real answer never retried.
   */
  let giveUpWaiting: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<typeof TIMED_OUT>((resolve) => {
    giveUpWaiting = setTimeout(() => resolve(TIMED_OUT), PROBE_DEADLINE_MS);
  });

  void (async () => {
    try {
      const status = await Promise.race([answered, deadline]);
      if (isSuperseded()) {
        return;
      }
      if (status === TIMED_OUT) {
        /* `warn`, not `warnErrorLike`: there is no error here, and the latter stringifies undefined. */
        void Logger.warn(
          "Grant probe did not answer before the deadline",
          undefined,
          { metricType: "MacOsGrantProbeTimedOut" }
        );
        settleUnanswered();
        return;
      }
      /*
       * The answer crosses a process boundary, so its shape is the host's promise rather than
       * something the compiler checked. A payload without the list is treated as no answer at all,
       * which shows the walkthrough, rather than as an empty one, which would claim both grants.
       */
      if (!Array.isArray(status?.missingGrants)) {
        void Logger.warn(
          "Grant probe answered without a grant list",
          undefined,
          { metricType: "MacOsGrantProbeMalformed" }
        );
        settleUnanswered();
        return;
      }
      cancelRetry();
      retriesLeft = MAX_RETRIES;
      publish({
        didProbeAnswer: true,
        isAnotherAttemptQueued: false,
        isResolved: true,
        missingGrants: status.missingGrants,
      });
    } catch (error) {
      /* Guarded before logging, as the answer path is: this re-probes on every focus, and a
         rejection nobody acted on is not a failure worth a metric. */
      if (isSuperseded()) {
        return;
      }
      Logger.warnErrorLike("Grant probe failed", error, {
        metricType: "MacOsGrantProbeFailed",
      });
      settleUnanswered();
    } finally {
      // The race's loser stays armed otherwise, one stray timer per probe per focus.
      clearTimeout(giveUpWaiting);
    }
  })();
}

/** A hire coming back from System Settings gets a fresh set of attempts, not the tail of the old one. */
const refreshShared = (): void => {
  cancelRetry();
  retriesLeft = MAX_RETRIES;
  probeOnce();
};

/**
 * Forget everything the store holds. For tests.
 *
 * The state below is module-scoped, and a test file cannot unload it between cases -- so without this
 * a subscriber, a retry budget or an answer from one case silently decides the next one's result.
 * `latestProbe` advances rather than resetting, so anything still in flight stays superseded.
 */
export const resetGrantStatusStore = (): void => {
  cancelRetry();
  listeners.clear();
  latestProbe += 1;
  retriesLeft = MAX_RETRIES;
  snapshot = UNREAD;
};

/**
 * The macOS grants, re-read whenever the hire could have changed them.
 *
 * macOS gives no change notification, so this listens for window focus: the hire has to leave for
 * System Settings and come back. An absent bridge resolves with an empty list and `didProbeAnswer`
 * false -- callers must read the pair, because the list alone cannot say whether a grant is held.
 */
export const useGrantStatus = (): GrantStatus => {
  const [current, setCurrent] = useState<GrantSnapshot>(snapshot);

  useEffect(() => {
    listeners.add(setCurrent);
    /* The first reader starts the probe and owns the focus listener; later ones join its answer. */
    if (listeners.size === 1) {
      /*
       * Focus is only worth listening to where there is something to re-read. A build with no probe --
       * every hire outside the rollout -- would otherwise arm a listener and settle again on each
       * focus, always to the same answer. Gated on the probe alone rather than on the whole permission
       * flow, so a host that can read the grants but not open Settings is still read.
       */
      if (typeof window.electronAPI?.probeAgentCapability === "function") {
        window.addEventListener("focus", refreshShared);
      }
      refreshShared();
    } else {
      setCurrent(snapshot);
    }

    return () => {
      listeners.delete(setCurrent);
      if (listeners.size > 0) {
        return;
      }
      window.removeEventListener("focus", refreshShared);
      cancelRetry();
      /*
       * Supersedes anything still in flight, and forgets the answer: the next mount is a new page as
       * far as the grants go, and macOS can have changed them while nothing was reading.
       */
      latestProbe += 1;
      retriesLeft = MAX_RETRIES;
      snapshot = UNREAD;
    };
  }, []);

  return {
    areGrantsHeld:
      !isPermissionFlowAvailable() ||
      (current.didProbeAnswer && current.missingGrants.length === 0),
    didProbeAnswer: current.didProbeAnswer,
    isAnotherAttemptQueued: current.isAnotherAttemptQueued,
    isResolved: current.isResolved,
    missingGrants: current.missingGrants,
    refresh: refreshShared,
  };
};
